import { useCallback } from 'react';

import type { DxfLayer } from '@frontend/api';
import {
  Checkbox,
  Empty,
  Flex,
  Text,
  VStack,
  EmptyHeadline,
  type VStackProps,
  Badge,
} from '@frontend/legacy-components';

export type DxfLayerPanelProps = {
  /** Layers from the DXF scene (`scene.layers`), rendered top-to-bottom. */
  layers: readonly DxfLayer[];
  /** Layer indices to hide; an index absent from the set is visible. */
  hiddenLayers: ReadonlySet<number>;
  /** Toggle a single layer. */
  onToggleLayer: (layerIndex: number, nextVisible: boolean) => void;
  /** Clicking the layer's name selects its geometry; Shift adds to the selection. */
  onSelectLayer: (layerIndex: number, isAdditive: boolean) => void;
  /** Double-clicking the name frames the layer. */
  onFrameLayer: (layerIndex: number) => void;
  /** Hovering a row highlights the layer on the canvas; `null` on leave. */
  onHoverLayer: (layerIndex: number | null) => void;
  /** Per-layer source-entity counts, keyed by layer index, shown as a badge. */
  counts: ReadonlyMap<number, number>;
  /** Layers that have stitched geometry, and so can be selected by name. */
  selectableLayers: ReadonlySet<number>;
  /** Selected source entities per layer index. Only layers carrying some of the
   * selection have an entry, so `has` reads as "this layer is in the selection"
   * and `get` gives how much of it — deliberately the same units as `counts`, so
   * the two can be shown against each other as "5 of 43".
   */
  selectedLayers: ReadonlySet<number>;
  /**
   * Layers that are hidden but still showing something — PMI outlives a layer
   * toggle — so their checkbox reads as neither on nor off.
   */
  indeterminateLayers: ReadonlySet<number>;
} & VStackProps;

type DxfLayerRowProps = {
  layerIndex: number;
  layerName: string;
  layerColor: string;
  isVisible: boolean;
  count?: number;
  /** Hidden, but still drawing something. */
  isIndeterminate?: boolean;
  /** Toggle a single layer. */
  onToggleLayer: (layerIndex: number, nextVisible: boolean) => void;
  /** Clicking the layer's name selects its geometry; Shift adds to the selection. */
  onSelectLayer: (layerIndex: number, isAdditive: boolean) => void;
  /** Double-clicking the name frames the layer. */
  onFrameLayer: (layerIndex: number) => void;
  /** Hovering a row highlights the layer on the canvas; `null` on leave. */
  onHoverLayer: (layerIndex: number | null) => void;
  /** A layer with no stitched geometry has nothing to select, so it stays inert. */
  isSelectable?: boolean;
  /** Whether the layer is selected. */
  isSelected: boolean;
};

/**
 * A single layer row
 */
const DxfLayerRow = ({
  layerIndex,
  layerName,
  layerColor,
  isVisible,
  count,
  onToggleLayer,
  isIndeterminate = false,
  onSelectLayer,
  onFrameLayer,
  onHoverLayer,
  isSelectable = false,
  isSelected,
}: DxfLayerRowProps) => {
  const handleToggleLayer = useCallback(() => {
    onToggleLayer(layerIndex, !isVisible);
  }, [onToggleLayer, layerIndex, isVisible]);

  // The name is its own control so that clicking it selects rather than toggling
  // visibility, while the rest of the row stays the checkbox's own label.
  const handleSelectLayer = useCallback(
    (event: React.MouseEvent) => {
      if (!isSelectable) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.detail > 1) {
        onFrameLayer(layerIndex);
        return;
      }
      onSelectLayer(layerIndex, event.shiftKey);
    },
    [onSelectLayer, onFrameLayer, layerIndex, isSelectable],
  );

  const handleHoverLayer = useCallback(() => {
    onHoverLayer(layerIndex);
  }, [onHoverLayer, layerIndex]);

  const handleUnhoverLayer = useCallback(() => {
    onHoverLayer(null);
  }, [onHoverLayer]);

  return (
    <Flex
      align="center"
      px={1.5}
      py={0.75}
      gap={1.5}
      bg={isSelected ? 'interaction.surfaceActive' : 'page.surface'}
      _hover={
        isSelectable && !isSelected
          ? { bg: 'interaction.surfaceHover' }
          : undefined
      }
    >
      <Checkbox
        size="md"
        accentColor={layerColor}
        isChecked={isVisible}
        isIndeterminate={isIndeterminate}
        onChange={handleToggleLayer}
        aria-label={`Toggle ${layerName} visibility`}
      />
      <Flex
        as="button"
        type="button"
        flex={1}
        minW={0}
        disabled={!isSelectable}
        aria-label={`Select layer ${layerName}`}
        aria-pressed={isSelected}
        align="center"
        justify="space-between"
        gap={1.5}
        color={isSelected ? 'interaction.main' : 'feature.secondary'}
        cursor={isSelectable ? 'pointer' : 'default'}
        transitionProperty="color"
        transitionDuration="0.15s"
        transitionTimingFunction="ease-in-out"
        _hover={isSelectable ? { color: 'interaction.main' } : undefined}
        onClick={handleSelectLayer}
        onMouseEnter={handleHoverLayer}
        onMouseLeave={handleUnhoverLayer}
      >
        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
          {layerName}
        </Text>
        <Badge
          flexShrink={0}
          bg="page.plateMedium"
          borderColor="page.plateMedium"
          color="feature.secondary"
        >
          {count}
        </Badge>
      </Flex>
    </Flex>
  );
};

export const DxfLayerPanel = ({
  layers,
  hiddenLayers,
  onToggleLayer,
  onSelectLayer,
  onFrameLayer,
  onHoverLayer,
  counts,
  selectableLayers,
  indeterminateLayers,
  selectedLayers,
  ...props
}: DxfLayerPanelProps) => {
  return (
    <VStack spacing={0} align="stretch" {...props}>
      {/* This should never happen, but handle it gracefully if it ever does */}
      {layers.length === 0 ? (
        <Empty size="sm" px={4} py={2}>
          <EmptyHeadline>No layers</EmptyHeadline>
        </Empty>
      ) : (
        layers.map((layer, index) => (
          <DxfLayerRow
            key={layer.name}
            layerIndex={index}
            layerName={layer.name}
            layerColor={layer.style.rgb}
            isVisible={!hiddenLayers.has(index)}
            count={counts.get(index)}
            isSelectable={selectableLayers.has(index)}
            isSelected={selectedLayers.has(index)}
            isIndeterminate={indeterminateLayers.has(index)}
            onToggleLayer={onToggleLayer}
            onSelectLayer={onSelectLayer}
            onFrameLayer={onFrameLayer}
            onHoverLayer={onHoverLayer}
          />
        ))
      )}
    </VStack>
  );
};
